import { createAsyncThunk } from '@reduxjs/toolkit'

import type { RootState } from '../../index'
import { apiPOST } from '../../lib/api'

export type DefectAssistantSuggestion = {
  // Defect code prefix, e.g. "GECB"
  defectCode: string
  // Path before the label, segments joined with " > " (may be empty)
  category: string
  // Human-readable label for the prefix, e.g. "STM-keskusyksikkö"
  label: string
  // Empty from /comment: the "why to choose" defectCodeAdvice streams in and is fetched per prefix
  // afterwards via fetchDefectCodeAdvice (keyed by the response's generationId).
  description?: string
}

export type DefectAssistantImage = {
  base64: string
  mimeType: string
}

export type DefectAssistantSuggestionsResponse = {
  suggestions: DefectAssistantSuggestion[]
  // Backend-driven flow-control options ("none of these — refine/give up") shown after suggestions
  metaOptions: OptionValue[]
  // Identifies the in-flight suggestion stream so each prefix's defectCodeAdvice can be pulled separately.
  generationId: string | null
}

export type DefectAssistantDefectCodeAdvice = {
  prefix: string
  defectCodeAdvice: string | null
  // false ⇒ not available (timeout / expired / none produced); render the localized fallback.
  ready: boolean
}

export const OptionKinds = {
  ANSWER: 'ANSWER',
  DISCARD: 'DISCARD',
  DISCARD_AND_REFINE: 'DISCARD_AND_REFINE',
  GIVE_UP: 'GIVE_UP',
  FREE_TEXT: 'FREE_TEXT',
  NOTHING_TO_ADD: 'NOTHING_TO_ADD',
  ADD_COMMENT_TO_EXISTING_DEFECT: 'ADD_COMMENT_TO_EXISTING_DEFECT',
  CREATE_NEW_DEFECT: 'CREATE_NEW_DEFECT',
} as const

export type OptionKind = (typeof OptionKinds)[keyof typeof OptionKinds]

export const AnswerTypes = {
  SINGLE: 'SINGLE',
  MULTISELECT: 'MULTISELECT',
} as const

export type AnswerType = (typeof AnswerTypes)[keyof typeof AnswerTypes]

export const QuestionTypes = {
  DEFECT_TYPE: 'defectType',
  PHYSICAL_ITEM: 'physicalItem',
  COMPLETENESS: 'completeness',
  DUPLICATE_CHECK: 'duplicateCheck',
} as const

/** Sentinel value used by the physicalItem question to indicate "no item selected". */
export const PHYSICAL_ITEM_NONE_VALUE = '__NONE__'

export type OptionValue = {
  label: string
  value: string | number
  additionalInfo?: string | null
  kind: OptionKind
}

export type MatchedDefect = {
  id: string
  defectCode: string
  defectType: string
  description: string | null
  comments: string | null
  issuedTime: string | null
  status: string
  hasNewInformation: boolean
  suggestedComment: string | null
  reason: string
}

export type QnA = {
  uuid: string
  questionType: string
  question: string
  options: OptionValue[]
  answerType: AnswerType
  answers?: OptionValue[] | null
  summary?: string | null
  matchedDefect?: MatchedDefect | null
}

export type ReportDefectBody = {
  userComment: string
  aiComment: string
  defectCode: string
  defectType: string
  installationGroup: string
  vehicleSeries: string
  // undefined: backend omitted the field; null: backend explicitly cleared a resolved item id.
  itemId?: string | null
}

export type ReportDefectQuestion = {
  body: ReportDefectBody
  questionAnswers: QnA[]
}

/**
 * Mirrors the backend's QnA.isUnanswered(): a question is unanswered exactly when its answers list
 * is null or empty, uniformly across all answer types. The multi-select completeness question carries
 * an explicit NOTHING_TO_ADD option, so "the description is already complete" is submitted as a
 * non-empty list rather than relying on an empty-vs-null distinction.
 */
export const isQuestionUnanswered = (qna: QnA): boolean =>
  qna.answers == null || qna.answers.length === 0

const isAnswered = (qna: QnA): boolean => !isQuestionUnanswered(qna)

const AI_MAX_BASE64_BYTES = 5 * 1024 * 1024

const compressImageForAi = (image: DefectAssistantImage): Promise<DefectAssistantImage> => {
  if (image.base64.length <= AI_MAX_BASE64_BYTES) return Promise.resolve(image)

  return new Promise((resolve) => {
    const img = new window.Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        resolve(image)
        return
      }
      ctx.drawImage(img, 0, 0)
      let quality = 0.85
      let compressed = canvas.toDataURL('image/jpeg', quality).split(',')[1]
      while (compressed.length > AI_MAX_BASE64_BYTES && quality > 0.1) {
        quality = Math.round((quality - 0.1) * 10) / 10
        compressed = canvas.toDataURL('image/jpeg', quality).split(',')[1]
      }
      resolve({ base64: compressed, mimeType: 'image/jpeg' })
    }
    img.onerror = () => resolve(image)
    img.src = `data:${image.mimeType};base64,${image.base64}`
  })
}

export const analyzeDefect = async (
  comment: string,
  images: DefectAssistantImage[],
  vehicleType: string,
  serialNumber: string,
  isRetry: boolean,
  discardedDefectCodes: string[]
): Promise<DefectAssistantSuggestionsResponse> => {
  const response = await apiPOST<DefectAssistantSuggestionsResponse>('/defectAssistant/comment', {
    comment,
    images,
    vehicleType,
    serialNumber,
    isRetry,
    discardedDefectCodes,
  })
  return {
    suggestions: response.suggestions ?? [],
    metaOptions: response.metaOptions ?? [],
    generationId: response.generationId ?? null,
  }
}

export const fetchDefectCodeAdvice = async (
  generationId: string,
  prefix: string
): Promise<DefectAssistantDefectCodeAdvice> => {
  return await apiPOST<DefectAssistantDefectCodeAdvice>('/defectAssistant/defectCodeAdvice', {
    generationId,
    prefix,
  })
}

export const getNextDefectQuestion = async (
  body: ReportDefectBody,
  questionAnswers: QnA[],
  images: DefectAssistantImage[]
): Promise<ReportDefectQuestion> => {
  return await apiPOST<ReportDefectQuestion>('/defectAssistant/questions', {
    body,
    questionAnswers,
    images,
  })
}

type QuestionFlowResult = {
  body: ReportDefectBody
  questionAnswers: QnA[]
}

export const analyzeSuggestionsAction = createAsyncThunk<
  DefectAssistantSuggestionsResponse,
  void,
  { state: RootState; rejectValue: string }
>('createDefectAssistant/analyzeSuggestions', async (_, thunkApi) => {
  const state = thunkApi.getState().createDefectAssistant
  try {
    // The backend resolves and validates suggestions against its own defect tree, so the client
    // renders them as-is (no local tree filtering anymore).
    const response = await analyzeDefect(
      state.body.userComment,
      await Promise.all(state.images.map(compressImageForAi)),
      state.body.vehicleSeries,
      state.body.installationGroup,
      state.suggestionAttempts > 1,
      state.discardedDefectCodes
    )
    // Fan out one defectCodeAdvice fetch per prefix; each resolves independently (first-ready-first) and
    // updates its button. generationId is passed explicitly so it doesn't depend on slice timing.
    if (response.generationId) {
      const generationId = response.generationId
      response.suggestions.forEach((s) => {
        void thunkApi.dispatch(fetchDefectCodeAdviceAction({ generationId, prefix: s.defectCode }))
      })
    }
    return response
  } catch {
    return thunkApi.rejectWithValue('Failed to analyze defect suggestions')
  }
})

export const fetchDefectCodeAdviceAction = createAsyncThunk<
  DefectAssistantDefectCodeAdvice,
  { generationId: string; prefix: string },
  { rejectValue: string }
>('createDefectAssistant/fetchDefectCodeAdvice', async ({ generationId, prefix }, thunkApi) => {
  try {
    return await fetchDefectCodeAdvice(generationId, prefix)
  } catch {
    // The slice reads the prefix from action.meta.arg, so a plain reject value is enough here.
    return thunkApi.rejectWithValue('Failed to fetch defectCodeAdvice')
  }
})

export const advanceQuestionFlowAction = createAsyncThunk<
  QuestionFlowResult,
  void,
  { state: RootState; rejectValue: string }
>('createDefectAssistant/advanceQuestionFlow', async (_, thunkApi) => {
  const state = thunkApi.getState().createDefectAssistant

  try {
    const updatedQuestionAnswers = state.questionAnswers
    const updatedBody = state.body

    // Resend the attached images on every questions turn (mirroring analyzeDefect) — the pipeline's
    // completeness/finalization steps need them, otherwise an image-only report stalls after item
    // selection with no comment and no images to work from.
    const response = await getNextDefectQuestion(
      updatedBody,
      updatedQuestionAnswers,
      await Promise.all(state.images.map(compressImageForAi))
    )
    const body = response.body
    const completed =
      response.questionAnswers.length === 0 || response.questionAnswers.every(isAnswered)

    return {
      body,
      questionAnswers:
        completed && response.questionAnswers.length === 0
          ? updatedQuestionAnswers
          : response.questionAnswers,
    }
  } catch {
    return thunkApi.rejectWithValue('Failed to fetch next defect question')
  }
})

export type DefectAssistantValidateRequest = {
  description: string
  defectCode: string
  defectGroup?: string
  vehicleType: string
  serialNumber: string
}

export type DefectAssistantValidateResponseSeverity = 'ok' | 'warning' | 'error'

export type DefectAssistantValidateResponse = {
  severity: DefectAssistantValidateResponseSeverity
  reasoning: string
  suggestedFixes: string[]
}

export const validateDefectInfo = async (
  payload: DefectAssistantValidateRequest
): Promise<DefectAssistantValidateResponse> => {
  return await apiPOST<DefectAssistantValidateResponse>('/defectAssistant/validate', payload)
}

/**
 * Fire-and-forget pre-warming of the AI defect-assistant caches for a vehicle, so the later
 * suggestion call is fast. Call it when the worker selects a vehicle (and on form-land with a
 * preselected vehicle). Best-effort: failures are swallowed and never block the form.
 */
export const cacheWarmup = (vehicleType: string, serialNumber: string): void => {
  if (!vehicleType || !serialNumber) return
  void apiPOST<{ status: string }>('/defectAssistant/cacheWarmup', {
    vehicleType,
    serialNumber,
  }).catch(() => {
    // Non-critical optimisation — ignore failures.
  })
}
